import aiService from '../lib/aiService.js'
import { imageService } from '../lib/imageService.js'
import { determineVisualStyle } from './visualStyle.js'
import { supabase } from '../lib/supabase.js'

// Helper function to get reference image for backgrounds (cycles through different images)
const getReferenceImageForSlide = async (slideType, slideIndex, advancedData) => {
  if (!advancedData?.referenceImages || advancedData.referenceImages.length === 0) {
    return null
  }

  try {
    // Use slideIndex to cycle through different reference images for variety
    const imageIndex = slideIndex % advancedData.referenceImages.length
    const imageId = advancedData.referenceImages[imageIndex]

    console.log(`🔄 Using reference image ${imageIndex + 1} of ${advancedData.referenceImages.length} for ${slideType} slide`)

    const { data: imageRecord, error } = await supabase
      .from('user_image_library')
      .select('file_url')
      .eq('id', imageId)
      .single()

    if (!error && imageRecord?.file_url) {
      console.log(`🖼️ SUCCESS: Using reference image for ${slideType} slide background`)
      console.log(`📸 Image URL: ${imageRecord.file_url}`)
      return imageRecord.file_url
    } else {
      console.warn(`⚠️ Reference image not found for ${slideType} slide. ImageId: ${imageId}, Error:`, error)
    }
  } catch (error) {
    console.error('Error fetching reference image:', error)
  }

  return null
}

// Helper function to determine text color based on theme
const getTextColor = (theme, isHeading = false) => {
  // For light backgrounds (like comedy/family), use dark text
  if (theme.primary && (theme.primary.includes('fef') || theme.primary.includes('fff'))) {
    return isHeading ? theme.textDark : theme.textLight
  }
  // For dark backgrounds, use light text
  return '#ffffff'
}

// Generate theme colors and backgrounds based on project genre
const getGenreTheme = (analysis) => {
  const genres = analysis.genres || ['Drama']
  const mainGenre = genres[0]?.toLowerCase() || 'drama'
  
  switch (mainGenre) {
    case 'horror':
    case 'thriller':
    case 'psychological horror':
      return {
        primary: '#1f1f1f', // Dark background
        secondary: '#dc2626', // Blood red accent
        textLight: '#ffffff',
        textDark: '#fef2f2',
        gradient: 'linear-gradient(135deg, #1f1f1f 0%, #7f1d1d 100%)'
      }
    
    case 'sci-fi':
    case 'science fiction':
    case 'cyberpunk':
    case 'futuristic':
      return {
        primary: '#0f172a', // Deep space blue
        secondary: '#3b82f6', // Electric blue
        textLight: '#ffffff',
        textDark: '#e0f2fe',
        gradient: 'linear-gradient(135deg, #0f172a 0%, #1e40af 100%)'
      }
    
    case 'fantasy':
    case 'magical':
    case 'supernatural':
      return {
        primary: '#4c1d95', // Deep purple
        secondary: '#fbbf24', // Golden accent
        textLight: '#ffffff',
        textDark: '#fef3c7',
        gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)'
      }
    
    case 'comedy':
    case 'romantic comedy':
    case 'family':
      return {
        primary: '#fef3c7', // Light yellow
        secondary: '#f59e0b', // Orange accent
        textLight: '#92400e',
        textDark: '#451a03',
        gradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
      }
    
    case 'action':
    case 'adventure':
    case 'war':
      return {
        primary: '#374151', // Steel gray
        secondary: '#ef4444', // Bold red
        textLight: '#ffffff',
        textDark: '#f3f4f6',
        gradient: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)'
      }
    
    case 'romance':
    case 'romantic':
      return {
        primary: '#fce7f3', // Soft pink
        secondary: '#ec4899', // Rose accent
        textLight: '#831843',
        textDark: '#500724',
        gradient: 'linear-gradient(135deg, #fce7f3 0%, #f9a8d4 100%)'
      }
    
    case 'documentary':
    case 'historical':
    case 'biographical':
      return {
        primary: '#f3f4f6', // Light gray
        secondary: '#6b7280', // Medium gray
        textLight: '#1f2937',
        textDark: '#111827',
        gradient: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
      }
    
    default: // Drama and others
      return {
        primary: '#ffffff', // Clean white
        secondary: '#3b82f6', // Professional blue
        textLight: '#1f2937',
        textDark: '#111827',
        gradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
      }
  }
}


// Utility function to ensure synopsis is clean text with proper paragraph formatting and approximately 110 words
const cleanSynopsisContent = (synopsis) => {
  console.log('🔍 Synopsis received for cleaning:', typeof synopsis, synopsis?.substring(0, 100))
  
  // If it's not a string, convert it
  if (typeof synopsis !== 'string') {
    console.log('⚠️ Synopsis is not a string, converting')
    synopsis = String(synopsis || 'A compelling narrative exploring important themes through engaging characters.')
  }
  
  // Clean up any JSON artifacts first
  if (synopsis.includes('{') || synopsis.includes('"synopsis"') || synopsis.includes('```')) {
    console.log('⚠️ Synopsis contains JSON artifacts, attempting to clean')
    
    // Try to extract synopsis from JSON string
    try {
      if (synopsis.includes('"synopsis"')) {
        const match = synopsis.match(/"synopsis":\s*"([^"]+)"/)
        if (match && match[1]) {
          synopsis = match[1].trim()
          console.log('✅ Extracted synopsis from JSON pattern')
        }
      }
    } catch (e) {
      console.log('⚠️ Failed to extract synopsis from JSON pattern')
      synopsis = 'A compelling narrative exploring important themes through engaging characters.'
    }
  }
  
  let cleanedSynopsis = synopsis.trim()
  
  // Target approximately 110 words
  const words = cleanedSynopsis.split(' ')
  const targetWordCount = 110
  
  if (words.length > targetWordCount + 10) {
    // If significantly longer than target, trim to approximately 110 words
    const trimmedWords = words.slice(0, targetWordCount)
    cleanedSynopsis = trimmedWords.join(' ')
    
    // Ensure it ends with proper punctuation
    if (!cleanedSynopsis.endsWith('.') && !cleanedSynopsis.endsWith('!') && !cleanedSynopsis.endsWith('?')) {
      cleanedSynopsis += '.'
    }
    
    console.log(`✅ Trimmed synopsis from ${words.length} to ${trimmedWords.length} words (target: ${targetWordCount})`)
  } else if (words.length < targetWordCount - 20) {
    // If significantly shorter than target, expand with additional content
    console.log(`ℹ️ Synopsis is ${words.length} words, which is shorter than target (${targetWordCount}). Keeping as is.`)
  } else {
    console.log(`✅ Synopsis length (${words.length} words) is close to target (${targetWordCount})`)
  }
  
  // Format for two paragraphs if it's long enough
  const sentences = cleanedSynopsis.split('. ')
  if (sentences.length >= 4) {
    // Split in the middle for two paragraphs
    const midPoint = Math.ceil(sentences.length / 2)
    const firstParagraph = sentences.slice(0, midPoint).join('. ') + (sentences.length > midPoint ? '.' : '')
    const secondParagraph = sentences.slice(midPoint).join('. ')
    
    if (secondParagraph.trim()) {
      console.log('✅ Split synopsis into two paragraphs')
      return `${firstParagraph}\n\n${secondParagraph}`
    }
  }
  
  console.log('✅ Synopsis is clean (single paragraph)')
  return cleanedSynopsis
}

// Helper function to create hybrid analysis using user data as priority
const createHybridAnalysis = (aiAnalysis, advancedData, title, prompt) => {
  console.log('🔄 Creating hybrid analysis with user data priority...')
  console.log('🎯 User provided title:', title)
  console.log('🎯 Advanced data received:', advancedData)
  
  // Start with AI analysis as base
  const hybridAnalysis = { ...aiAnalysis }
  
  // 🚨 CRITICAL: User's project title takes ABSOLUTE priority
  if (title && title.trim()) {
    hybridAnalysis.title = title.trim()
    console.log('✅ Using user title:', hybridAnalysis.title)
  }
  
  // Override with user-provided data (user data takes absolute priority)
  if (advancedData.projectType) {
    hybridAnalysis.format = advancedData.projectType
    console.log('✅ Using user project type:', advancedData.projectType)
  }
  
  if (advancedData.genre) {
    hybridAnalysis.genres = [advancedData.genre]
    console.log('✅ Using user genre:', advancedData.genre)
  }
  
  if (advancedData.outline && advancedData.outline.trim()) {
    // Use user outline as synopsis, but keep it formatted nicely
    hybridAnalysis.synopsis = advancedData.outline
    console.log('✅ Using user outline as synopsis')
  }
  
  // Replace AI characters with user characters if provided
  if (advancedData.characters && advancedData.characters.length > 0) {
    console.log('👥 Using user-provided characters instead of AI-generated ones')
    hybridAnalysis.characters = advancedData.characters.map(char => ({
      name: char.name,
      role: char.role,
      age: 30, // Default age
      traits: char.bio ? [char.bio.substring(0, 50)] : ['Compelling character'],
      appeal: `${char.role} with unique story significance`,
      description: char.bio || `${char.name} plays a crucial role as ${char.role}`,
      physicalDescription: char.bio || `Character appearance to be determined`,
      imageId: char.imageId // Store the uploaded image ID
    }))
  }
  
  // Use user locations in supporting cast or other content
  if (advancedData.locations && advancedData.locations.length > 0) {
    console.log('📍 Incorporating user-provided locations')
    // We can use these in the supporting cast or format details
    const locationDescriptions = advancedData.locations.map(loc => 
      `${loc.name}${loc.description ? ` - ${loc.description}` : ''}`
    )
    
    // Add location info to format details
    if (!hybridAnalysis.formatDetails) hybridAnalysis.formatDetails = []
    hybridAnalysis.formatDetails.push(`Filming locations: ${locationDescriptions.join(', ')}`)
  }
  
  // Add creator info to production approach
  if (advancedData.creator || advancedData.creatorCompany) {
    const creatorInfo = [advancedData.creator, advancedData.creatorCompany].filter(Boolean).join(' / ')
    if (!hybridAnalysis.productionApproach) hybridAnalysis.productionApproach = []
    hybridAnalysis.productionApproach.unshift(`Created by: ${creatorInfo}`)
  }
  
  // Add year setting info
  if (advancedData.yearSet) {
    hybridAnalysis.themes = hybridAnalysis.themes || []
    hybridAnalysis.themes.push(`Set in ${advancedData.yearSet}`)
  }
  
  // Add extra notes to relevant sections
  if (advancedData.extraNotes && advancedData.extraNotes.trim()) {
    if (!hybridAnalysis.productionApproach) hybridAnalysis.productionApproach = []
    hybridAnalysis.productionApproach.push(`Special notes: ${advancedData.extraNotes}`)
  }
  
  console.log('✅ Hybrid analysis created with user data prioritized')
  return hybridAnalysis
}

// AI-Powered Slide Deck Generation System
export const generateSlideFromPrompt = async (prompt, title, setProgressMessage, setProgressStep, userId = null, advancedData = {}) => {
  try {
    // Use AI service to analyze the prompt and generate content
    if (setProgressMessage) setProgressMessage('🧠 AI is analyzing your story concept...')
    if (setProgressStep) setProgressStep(2)
    
    const aiAnalysis = await aiService.generateSlideContent(prompt, title, 'prompt', null, advancedData)
    
    // Create hybrid analysis that prioritizes user data
    const analysis = createHybridAnalysis(aiAnalysis, advancedData, title, prompt)
    
    // Debug logging to see what we're using
    console.log('🔍 Final analysis (user data prioritized):', {
      userCharacters: advancedData.characters?.length || 0,
      userLocations: advancedData.locations?.length || 0,
      userGenre: advancedData.genre,
      userProjectType: advancedData.projectType,
      finalCharacters: analysis.characters?.length || 0,
      finalGenre: analysis.genres,
      finalFormat: analysis.format
    })
    
    if (setProgressMessage) setProgressMessage('🎬 Creating professional slide structure...')
    if (setProgressStep) setProgressStep(3)
    
    // Generate complete slide deck using hybrid analysis (user data prioritized)
    const slides = [
      generateCoverSlide(analysis, advancedData),
      generateSynopsisSlide(analysis, advancedData),
      await generateCharactersSlideWithAI(analysis, title, userId, advancedData),
      await generateSupportingCastSlideWithAI(analysis, title, userId, advancedData),
      await generateFormatStructureSlide(analysis, title, userId),
      await generateProductionApproachSlideWithAI(analysis, title, userId, advancedData),
      await generateTalentStrategySlideWithAI(analysis, title, userId, advancedData),
      await generateBudgetSlideWithAI(analysis, title, userId, advancedData),
      await generateContactSlide(analysis, userId, advancedData),
    ]
    
    return slides
  } catch (error) {
    console.error('Error generating slides from prompt:', error)
    
    // Fallback to original analysis method
    const analysis = analyzePrompt(prompt, title)
    
    const slides = [
      generateCoverSlide(analysis),
      generateSynopsisSlide(analysis),
      generateCharactersSlide(analysis),
      generateSupportingCastSlide(analysis),
      await generateFormatStructureSlide(analysis, 'Fallback Project', userId),
      generateProductionApproachSlide(analysis),
      generateTalentStrategySlide(analysis),
      await generateBudgetSlideWithAI(analysis, title || 'Fallback Project', userId),
      await generateContactSlide(analysis, userId, advancedData),
    ]
    
    return slides
  }
}

export const generateSlideFromScript = async (scriptFile, title, setProgressMessage, setProgressStep, userId = null, advancedData = {}) => {
  try {
    // Read script content if it's a file
    if (setProgressMessage) setProgressMessage('📖 Reading script content...')
    if (setProgressStep) setProgressStep(2)
    
    let scriptContent = ''
    if (scriptFile instanceof File) {
      scriptContent = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(scriptFile)
      })
    } else {
      scriptContent = scriptFile.toString()
    }

    if (setProgressMessage) setProgressMessage('🤖 AI is analyzing script structure...')
    if (setProgressStep) setProgressStep(3)

    // Use AI to analyze script content
    const aiAnalysis = await aiService.generateSlideContent(
      'Analyze this script and create a professional pitch deck',
      title,
      'script',
      scriptContent.substring(0, 8000),
      advancedData
    )
    
    // Create hybrid analysis that prioritizes user data over script analysis
    const analysis = createHybridAnalysis(aiAnalysis, advancedData, title, `Script: ${scriptContent.substring(0, 200)}...`)
    
    // Generate slides based on hybrid analysis (user data prioritized)
    const slides = [
      generateCoverSlide(analysis, advancedData),
      generateSynopsisSlide(analysis, advancedData),
      await generateCharactersSlideWithAI(analysis, title, userId, advancedData),
      await generateSupportingCastSlideWithAI(analysis, title, userId, advancedData),
      await generateFormatStructureSlide(analysis, title, userId),
      await generateProductionApproachSlideWithAI(analysis, title, userId, advancedData),
      await generateTalentStrategySlideWithAI(analysis, title, userId, advancedData),
      await generateBudgetSlideWithAI(analysis, title, userId, advancedData),
      await generateContactSlide(analysis, userId, advancedData),
    ]
    
    return slides
  } catch (error) {
    console.error('Error generating slides from script:', error)
    
    // Fallback to mock analysis
    const scriptAnalysis = analyzeScript(scriptFile, title)
    
    const slides = [
      generateCoverSlide(scriptAnalysis),
      generateSynopsisSlide(scriptAnalysis),
      generateCharactersSlide(scriptAnalysis),
      generateSupportingCastSlide(scriptAnalysis),
      generateFormatStructureSlide(scriptAnalysis),
      generateProductionApproachSlide(scriptAnalysis),
      generateTalentStrategySlide(scriptAnalysis),
      await generateBudgetSlideWithAI(scriptAnalysis, title || 'Script Project', userId)
    ]
    
    return slides
  }
}

const analyzePrompt = (prompt, title) => {
  // AI analysis simulation - extract key elements
  const genres = extractGenres(prompt)
  const characters = extractCharacters(prompt, title)
  const tone = extractTone(prompt)
  const format = determineFormat(prompt)
  
  return {
    title,
    genres,
    characters,
    tone,
    format,
    tagline: generateTagline(prompt, genres, tone),
    synopsis: generateSynopsis(prompt),
    targetAudience: extractTargetAudience(prompt),
    themes: extractThemes(prompt)
  }
}

const analyzeScript = (scriptFile, title) => {
  // Mock script analysis
  return {
    title,
    genres: ['Drama', 'Thriller'],
    characters: [
      { name: 'Sarah Mitchell', role: 'Protagonist', age: 35 },
      { name: 'David Chen', role: 'Antagonist', age: 42 }
    ],
    tone: 'Dark and Suspenseful',
    format: 'Feature Film',
    tagline: 'Some secrets are worth killing for',
    synopsis: 'A compelling story of betrayal and redemption...',
    targetAudience: 'Adults 25-54',
    themes: ['Betrayal', 'Redemption', 'Family']
  }
}

const generateCoverSlide = (analysis, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  console.log('🎬 Generating cover slide with title:', analysis.title)
  console.log('🎬 Advanced data for cover:', {
    creator: advancedData.creator,
    yearSet: advancedData.yearSet
  })
  
  // Create cover slide elements with user data priority
  const elements = [
    {
      id: `title-${Date.now()}`,
      type: 'text',
      content: analysis.title ? analysis.title.toUpperCase() : 'UNTITLED PROJECT',
      style: {
        fontSize: '42px',
        fontWeight: 'bold',
        color: '#ffffff',
        textAlign: 'center',
        position: { x: 50, y: 120 },
        width: 700,
        height: 100,
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
      }
    },
    {
      id: `tagline-${Date.now()}`,
      type: 'text',
      content: analysis.tagline,
      style: {
        fontSize: '24px',
        fontStyle: 'italic',
        color: '#e5e7eb',
        textAlign: 'center',
        position: { x: 205, y: 240 },
        width: 343,
        height: 60,
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
      }
    },
    {
      id: `format-${Date.now()}`,
      type: 'text',
      content: `${analysis.format}`,
      style: {
        fontSize: '18px',
        color: '#d1d5db',
        textAlign: 'center',
        position: { x: 50, y: 460 },
        width: 700,
        height: 40
      }
    },
    {
      id: `genre-${Date.now()}`,
      type: 'text',
      content: `Genre: ${(analysis.genres || ['Drama']).join(' | ')}`,
      style: {
        fontSize: '16px',
        color: '#9ca3af',
        textAlign: 'center',
        position: { x: 50, y: 500 },
        width: 700,
        height: 30
      }
    }
  ]

  // Add creator information if provided by user
  if (advancedData.creator || advancedData.creatorCompany) {
    const creatorText = [advancedData.creator, advancedData.creatorCompany].filter(Boolean).join(' / ')
    elements.push({
      id: `creator-${Date.now()}`,
      type: 'text',
      content: `Created by: ${creatorText}`,
      style: {
        fontSize: '14px',
        color: '#9ca3af',
        textAlign: 'center',
        position: { x: 50, y: 530 },
        width: 700,
        height: 20
      }
    })
  }

  // Add year setting if provided by user
  if (advancedData.yearSet) {
    elements.push({
      id: `year-${Date.now()}`,
      type: 'text',
      content: `Set in ${advancedData.yearSet}`,
      style: {
        fontSize: '14px',
        color: '#9ca3af',
        textAlign: 'center',
        position: { x: 50, y: advancedData.creator || advancedData.creatorCompany ? 550 : 530 },
        width: 700,
        height: 20
      }
    })
  }
  
  return {
    id: `cover-${Date.now()}`,
    name: 'Cover Slide',
    type: 'cover',
    elements,
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateSynopsisSlide = (analysis, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  // Use user outline if provided, otherwise use AI synopsis
  const synopsisContent = advancedData.outline && advancedData.outline.trim() 
    ? advancedData.outline 
    : analysis.synopsis

  console.log('📝 Synopsis slide content source:', {
    hasUserOutline: !!(advancedData.outline && advancedData.outline.trim()),
    userOutlineLength: advancedData.outline?.length || 0,
    aiSynopsisLength: analysis.synopsis?.length || 0,
    usingUserContent: !!(advancedData.outline && advancedData.outline.trim())
  })
  
  return {
    id: `synopsis-${Date.now()}`,
    name: 'Synopsis',
    type: 'content',
    elements: [
      {
        id: `synopsis-title-${Date.now()}`,
        type: 'text',
        content: advancedData.outline && advancedData.outline.trim() ? 'PROJECT OUTLINE' : 'SYNOPSIS',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'left',
          position: { x: 50, y: 50 },
          width: 700,
          height: 50
        }
      },
      {
        id: `synopsis-content-${Date.now()}`,
        type: 'text',
        content: cleanSynopsisContent(synopsisContent),
        style: {
          fontSize: '18px',
          color: theme.textDark,
          textAlign: 'left',
          lineHeight: '1.6',
          position: { x: 50, y: 120 },
          width: 700,
          height: 400
        }
      }
    ],
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateCharactersSlide = (analysis) => {
  const mainCharacters = analysis.characters.slice(0, 2)
  const theme = getGenreTheme(analysis)
  
  return {
    id: `characters-${Date.now()}`,
    name: 'Characters',
    type: 'content',
    elements: [
      {
        id: `characters-title-${Date.now()}`,
        type: 'text',
        content: 'CHARACTERS',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 30 },
          width: 700,
          height: 50
        }
      },
      // First Character
      {
        id: `char1-image-${Date.now()}`,
        type: 'image',
        src: getCharacterImage(mainCharacters[0]),
        style: {
          position: { x: 50, y: 100 },
          width: 320,
          height: 200,
          borderRadius: '12px'
        }
      },
      {
        id: `char1-name-${Date.now()}`,
        type: 'text',
        content: `${mainCharacters[0].name.toUpperCase()} (${mainCharacters[0].role})`,
        style: {
          fontSize: '20px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'left',
          position: { x: 50, y: 320 },
          width: 320,
          height: 30
        }
      },
      {
        id: `char1-details-${Date.now()}`,
        type: 'list',
        content: generateCharacterDetails(mainCharacters[0]),
        style: {
          fontSize: '14px',
          color: theme.textDark,
          lineHeight: '1.5',
          position: { x: 50, y: 360 },
          width: 320,
          height: 200
        }
      },
      // Second Character (if exists)
      ...(mainCharacters[1] ? [
        {
          id: `char2-image-${Date.now()}`,
          type: 'image',
          src: getCharacterImage(mainCharacters[1]),
          style: {
            position: { x: 430, y: 100 },
            width: 320,
            height: 200,
            borderRadius: '12px'
          }
        },
        {
          id: `char2-name-${Date.now()}`,
          type: 'text',
          content: `${mainCharacters[1].name.toUpperCase()} (${mainCharacters[1].role})`,
          style: {
            fontSize: '20px',
            fontWeight: 'bold',
            color: theme.textLight,
            textAlign: 'left',
            position: { x: 430, y: 320 },
            width: 320,
            height: 30
          }
        },
        {
          id: `char2-details-${Date.now()}`,
          type: 'list',
          content: generateCharacterDetails(mainCharacters[1]),
          style: {
            fontSize: '14px',
            color: theme.textDark,
            lineHeight: '1.5',
            position: { x: 430, y: 360 },
            width: 320,
            height: 200
          }
        }
      ] : [])
    ],
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateSupportingCastSlide = (analysis) => {
  const theme = getGenreTheme(analysis)
  const supportingCast = analysis.supportingCast || [
    { name: 'Supporting Character 1', role: 'Mentor', description: 'Guides the protagonist' },
    { name: 'Supporting Character 2', role: 'Ally', description: 'Provides support' }
  ]

  const elements = [
    {
      id: `supporting-title-${Date.now()}`,
      type: 'text',
      content: 'SUPPORTING CAST',
      style: {
        fontSize: '36px',
        fontWeight: 'bold',
        color: theme.textLight,
        textAlign: 'center',
        position: { x: 50, y: 30 },
        width: 700,
        height: 50
      }
    }
  ]

  // Add supporting characters dynamically
  supportingCast.slice(0, 3).forEach((character, index) => {
    const yPos = 130 + (index * 180)
    const xPos = index === 2 ? 500 : 100
    const contentXPos = index === 2 ? 670 : 270
    const contentWidth = index === 2 ? 150 : 300

    elements.push(
      {
        id: `support${index + 1}-image-${Date.now()}`,
        type: 'image',
        src: getSupportingCharacterImage(index + 1),
        style: {
          position: { x: xPos, y: yPos },
          width: 150,
          height: 150,
          borderRadius: '12px'
        }
      },
      {
        id: `support${index + 1}-name-${Date.now()}`,
        type: 'text',
        content: `${character.name} (${character.role})`,
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'left',
          position: { x: contentXPos, y: yPos },
          width: contentWidth,
          height: 30
        }
      },
      {
        id: `support${index + 1}-desc-${Date.now()}`,
        type: 'text',
        content: character.description,
        style: {
          fontSize: '14px',
          color: theme.textDark,
          lineHeight: '1.4',
          position: { x: contentXPos, y: yPos + 60 },
          width: contentWidth,
          height: 120
        }
      }
    )
  })

  return {
    id: `supporting-cast-${Date.now()}`,
    name: 'Supporting Cast',
    type: 'content',
    elements,
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateFormatStructureSlide = async (analysis, projectTitle, userId = null) => {
  const theme = getGenreTheme(analysis)
  
  // Generate AI image for format slide
  let formatImage = 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=300&h=400&fit=crop'
  
  try {
    const visualStyle = determineVisualStyle(analysis)
    const formatPrompt = `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

Scene from "${projectTitle}". 

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${analysis.genres?.join(', ') || 'cinematic'} atmosphere
- Representative moment from the ${analysis.format || 'production'} showing the story world and characters

Technical: Portrait format (300x400), high quality, suitable for format slide.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure scene content in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
    
    console.log('🎨 Generating format structure image...')
    
    const imageData = await imageService.generateImage({
      prompt: formatPrompt,
      type: 'scene',
      userId: userId || 'slide-generator',
      projectTitle: projectTitle
    })
    
    if (imageData?.url) {
      formatImage = imageData.url
    }
  } catch (error) {
    console.error('Failed to generate format structure image:', error)
  }
  
  return {
    id: `format-${Date.now()}`,
    name: 'Format & Structure',
    type: 'content',
    elements: [
      {
        id: `format-title-${Date.now()}`,
        type: 'text',
        content: 'FORMAT & STRUCTURE',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'center',
          position: { x: 50, y: 50 },
          width: 700,
          height: 50
        }
      },
      {
        id: `format-details-${Date.now()}`,
        type: 'list',
        content: (() => {
          let content = null
          
          // Check if formatDetails exists and has meaningful content
          if (analysis.formatDetails && Array.isArray(analysis.formatDetails) && analysis.formatDetails.length > 0) {
            content = analysis.formatDetails
          } else if (analysis.formatDetails && typeof analysis.formatDetails === 'string' && analysis.formatDetails.trim().length > 0) {
            content = [analysis.formatDetails]
          } else {
            // Use fallback content
            content = getFormatDetails(analysis)
          }
          
          console.log('🔍 Format & Structure content debug:', {
            hasFormatDetails: !!analysis.formatDetails,
            formatDetailsType: typeof analysis.formatDetails,
            formatDetailsLength: analysis.formatDetails?.length,
            formatDetailsContent: analysis.formatDetails,
            fallbackContent: getFormatDetails(analysis),
            finalContent: content,
            finalContentType: typeof content,
            finalContentLength: content?.length
          })
          return content
        })(),
        style: {
          fontSize: '16px',
          color: theme.textDark,
          lineHeight: '1.6',
          position: { x: 300, y: 150 },
          width: 450,
          height: 400
        }
      },
      {
        id: `format-image-${Date.now()}`,
        type: 'image',
        src: formatImage,
        style: {
          position: { x: 50, y: 120 },
          width: 200,
          height: 300,
          borderRadius: '12px'
        }
      }
    ],
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateProductionApproachSlide = (analysis) => {
  const theme = getGenreTheme(analysis)
  
  return {
    id: `production-${Date.now()}`,
    name: 'Production Approach',
    type: 'content',
    elements: [
      {
        id: `production-title-${Date.now()}`,
        type: 'text',
        content: 'PRODUCTION APPROACH',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'center',
          position: { x: 50, y: 50 },
          width: 700,
          height: 50
        }
      },
    {
      id: `production-details-${Date.now()}`,
      type: 'list',
      content: analysis.productionApproach || [
        'Innovative production techniques',
        'Cost-effective approach',
        'High-quality output',
        'Efficient workflow',
        'Professional standards',
        'Scalable process'
      ],
      style: {
        fontSize: '16px',
        color: theme.textDark,
        lineHeight: '1.6',
        position: { x: 300, y: 150 },
        width: 450,
        height: 400
      }
    },
    {
      id: `production-image-${Date.now()}`,
      type: 'image',
      src: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=250&h=350&fit=crop',
      style: {
        position: { x: 50, y: 120 },
        width: 200,
        height: 300,
        borderRadius: '12px'
      }
    }
  ],
  background: {
    type: 'gradient',
    value: theme.gradient
  }
  }
}

const generateTalentStrategySlide = (analysis) => {
  const theme = getGenreTheme(analysis)
  
  return {
    id: `talent-${Date.now()}`,
    name: 'Talent Strategy',
    type: 'content',
    elements: [
      {
        id: `talent-title-${Date.now()}`,
        type: 'text',
        content: 'TALENT STRATEGY',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'center',
          position: { x: 50, y: 50 },
          width: 700,
          height: 50,
          textShadow: theme.textLight === '#ffffff' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none'
        }
      },
    {
      id: `talent-subtitle-${Date.now()}`,
      type: 'text',
      content: 'Influencer-Driven Casting',
      style: {
        fontSize: '24px',
        color: theme.textDark,
        textAlign: 'center',
        position: { x: 50, y: 120 },
        width: 700,
        height: 40,
        textShadow: theme.textLight === '#ffffff' ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
      }
    },
    {
      id: `talent-details-${Date.now()}`,
      type: 'list',
      content: analysis.talentStrategy || getTalentDetails(analysis),
      style: {
        fontSize: '16px',
        color: theme.textDark,
        lineHeight: '1.6',
        position: { x: 50, y: 200 },
        width: 700,
        height: 350,
        textShadow: theme.textLight === '#ffffff' ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
      }
    }
  ],
  background: {
    type: 'gradient',
    value: theme.gradient
  }
  }
}

// Helper functions
const extractGenres = (prompt) => {
  const genres = []
  const promptLower = prompt.toLowerCase()
  
  if (promptLower.includes('horror') || promptLower.includes('scary') || promptLower.includes('haunted')) genres.push('Horror')
  if (promptLower.includes('romance') || promptLower.includes('love') || promptLower.includes('relationship')) genres.push('Romance')
  if (promptLower.includes('thriller') || promptLower.includes('suspense') || promptLower.includes('mystery')) genres.push('Thriller')
  if (promptLower.includes('comedy') || promptLower.includes('funny') || promptLower.includes('humor')) genres.push('Comedy')
  if (promptLower.includes('drama') || promptLower.includes('emotional') || promptLower.includes('family')) genres.push('Drama')
  if (promptLower.includes('action') || promptLower.includes('fight') || promptLower.includes('chase')) genres.push('Action')
  if (promptLower.includes('sci-fi') || promptLower.includes('science fiction') || promptLower.includes('space') || promptLower.includes('future')) genres.push('Sci-Fi')
  if (promptLower.includes('fantasy') || promptLower.includes('magic') || promptLower.includes('supernatural')) genres.push('Fantasy')
  
  return genres.length > 0 ? genres : ['Drama']
}

const extractCharacters = (prompt, title) => {
  // AI character extraction simulation
  return [
    {
      name: extractMainCharacterName(prompt, title),
      role: 'Protagonist',
      age: 25,
      arc: 'Hero\'s Journey',
      traits: ['Determined', 'Brave', 'Complex'],
      appeal: 'Relatable underdog with hidden strength',
      description: 'A compelling protagonist who undergoes significant character development throughout the story. Faces internal and external conflicts that resonate with audiences, making them a memorable and engaging lead character.'
    },
    {
      name: extractAntagonistName(prompt),
      role: 'Antagonist',
      age: 35,
      arc: 'Fall from Grace',
      traits: ['Powerful', 'Manipulative', 'Charismatic'],
      appeal: 'Complex villain with understandable motives',
      description: 'A sophisticated antagonist whose motivations are rooted in believable circumstances. Their opposition to the protagonist creates meaningful conflict that drives the narrative forward while maintaining audience interest.'
    }
  ]
}

const extractMainCharacterName = (prompt, title) => {
  // Simple name extraction - in real AI this would be more sophisticated
  const names = ['Alex', 'Sarah', 'Maya', 'Jordan', 'Riley', 'Casey', 'Morgan', 'Taylor']
  return names[Math.floor(Math.random() * names.length)]
}

const extractAntagonistName = (prompt) => {
  const names = ['Victor', 'Helena', 'Marcus', 'Diana', 'Adrian', 'Vera', 'Damien', 'Selena']
  return names[Math.floor(Math.random() * names.length)]
}

const extractTone = (prompt) => {
  const promptLower = prompt.toLowerCase()
  if (promptLower.includes('dark') || promptLower.includes('gritty')) return 'Dark and Atmospheric'
  if (promptLower.includes('light') || promptLower.includes('fun')) return 'Light and Entertaining'
  if (promptLower.includes('serious') || promptLower.includes('dramatic')) return 'Serious and Dramatic'
  return 'Balanced and Engaging'
}

const determineFormat = (prompt) => {
  const promptLower = prompt.toLowerCase()
  if (promptLower.includes('series') || promptLower.includes('episodes')) return 'Limited Series'
  if (promptLower.includes('short') || promptLower.includes('micro')) return 'Short Film'
  if (promptLower.includes('feature') || promptLower.includes('movie')) return 'Feature Film'
  return 'Limited Series'
}

const generateTagline = (prompt, genres, tone) => {
  const taglines = [
    'Love conquers all... or does it?',
    'Some secrets are worth dying for',
    'The truth will set you free... or destroy you',
    'When love becomes obsession',
    'Nothing is as it seems',
    'The past never stays buried',
    'Destiny has a dark sense of humor',
    'Some bonds can never be broken'
  ]
  return taglines[Math.floor(Math.random() * taglines.length)]
}

const generateSynopsis = (prompt) => {
  // AI synopsis generation - simplified for demo
  return `In a world where nothing is as it seems, our protagonist must navigate dangerous waters of deception and betrayal. When long-buried secrets surface, they must choose between the life they've known and the truth that could destroy everything they hold dear. With time running out and enemies closing in, every decision could be their last. This compelling story explores themes of identity, loyalty, and the price of truth in a world where everyone has something to hide.`
}

const extractTargetAudience = (prompt) => {
  return 'Adults 18-35, fans of contemporary drama and supernatural romance'
}

const extractThemes = (prompt) => {
  return ['Identity', 'Betrayal', 'Redemption', 'Love vs. Duty']
}

const generateCharacterDetails = (character) => {
  // Handle user-provided characters vs AI-generated characters differently
  const isUserCharacter = character.bio || character.description?.length > 200
  
  if (isUserCharacter) {
    // For user characters, prioritize their bio/description
    const details = []
    
    if (character.age) details.push(`Age: ${character.age}`)
    if (character.role) details.push(`Role: ${character.role}`)
    
    // Add user's bio/description with smart truncation
    const userBio = character.bio || character.description || ''
    if (userBio) {
      const words = userBio.split(' ')
      // Limit to ~15-20 words for better fit
      const maxWords = Math.min(20, Math.max(10, words.length))
      const shortened = words.slice(0, maxWords).join(' ')
      details.push('', shortened)
    }
    
    return details
  } else {
    // For AI characters, use standard format
    const details = [
      `Age: ${character.age || 25}`,
      `Arc: ${character.arc || 'Character development journey'}`,
      `Key Traits: ${character.traits?.join(', ') || 'Complex, compelling, relatable'}`,
      `Audience Appeal: ${character.appeal || 'Engaging character with universal appeal'}`
    ]
    
    // Add character description if available
    if (character.description) {
      // Trim description to fit slide layout
      const words = character.description.split(' ')
      const maxWords = Math.max(12, Math.floor(words.length * 0.8))
      const shortened = words.slice(0, maxWords).join(' ')
      details.push('', shortened)
    }
    
    return details
  }
}

const getCoverImage = (analysis) => {
  // Return appropriate cover image based on genre
  const genres = analysis.genres || []
  if (genres.includes('Horror')) {
    return 'https://images.unsplash.com/photo-1520637736862-4d197d17c38a?w=800&h=600&fit=crop'
  } else if (genres.includes('Romance')) {
    return 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop'
  } else if (genres.includes('Sci-Fi')) {
    return 'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=800&h=600&fit=crop'
  }
  return 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&h=600&fit=crop'
}

const getCharacterImage = (character) => {
  // Return character images based on role
  if (character.role === 'Protagonist') {
    return 'https://images.unsplash.com/photo-1494790108755-2616c9c0c8fb?w=320&h=200&fit=crop'
  } else if (character.role === 'Antagonist') {
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=320&h=200&fit=crop'
  }
  return 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=320&h=200&fit=crop'
}

const getSupportingCharacterImage = (index) => {
  const images = [
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop'
  ]
  return images[index - 1] || images[0]
}

const getFormatDetails = (analysis) => {
  const format = analysis.format || 'Feature Film'
  
  if (format.includes('Micro') || format.includes('Series')) {
    return [
      'Series Type: Micro-Drama',
      'Episode Count: 25 Episodes',
      'Episode Length: 60-90 seconds',
      'Total Runtime: ~30 minutes',
      'Distribution: Vertical video optimized for mobile platforms',
      '',
      'Story Arc:',
      'Episodes 1-5: Discovery and Awakening',
      'Episodes 6-15: Resistance and Conflict',
      'Episodes 16-20: Deepening Bond and Revelation',
      'Episodes 21-25: Choice and Resolution'
    ]
  } else if (format === 'Limited Series') {
    return [
      'Series Type: Limited Series',
      'Episode Count: 8 Episodes',
      'Episode Length: 45-60 minutes',
      'Total Runtime: ~6-8 hours',
      'Distribution: Streaming platforms',
      'Target Platform: Netflix, Hulu, Amazon Prime',
      '',
      'Season Structure:',
      'Episodes 1-2: Setup and Inciting Incident',
      'Episodes 3-5: Rising Action and Complications',
      'Episodes 6-7: Climax and Major Revelations',
      'Episode 8: Resolution and Conclusion'
    ]
  } else if (format === 'Short Film') {
    return [
      'Format: Short Film',
      'Runtime: 15-30 minutes',
      'Distribution: Film festivals, online platforms',
      'Target Audience: Festival circuit',
      'Production Timeline: 4-6 weeks',
      'Budget Range: Independent',
      '',
      'Structure:',
      'Act I: Setup (5-8 minutes)',
      'Act II: Conflict (8-15 minutes)',
      'Act III: Resolution (2-7 minutes)'
    ]
  }
  
  return [
    'Format: Feature Film',
    'Runtime: 90-120 minutes',
    'Distribution: Theatrical release',
    'Target Audience: General audience',
    'Production Timeline: 12-16 weeks',
    'Budget Range: Mid-budget',
    '',
    'Three-Act Structure:',
    'Act I: Setup and Inciting Incident (25%)',
    'Act II: Rising Action and Midpoint (50%)',
    'Act III: Climax and Resolution (25%)'
  ]
}

const getTalentDetails = (analysis) => {
  return [
    `Option A: Rising Social Media Stars`,
    `• ${analysis.characters[0].name}: @socialmedia_star (250K followers)`,
    `• ${analysis.characters[1].name}: @trending_actor (180K followers)`,
    '',
    `Option B: Established Influencers`,
    `• ${analysis.characters[0].name}: @major_influencer (1.2M followers)`,
    `• ${analysis.characters[1].name}: @content_creator (890K followers)`,
    '',
    'Combined Reach: 2.5M+ followers across platforms',
    'Built-in Audience: Immediate engagement and shareability'
  ]
}

// Enhanced AI-powered slide generation functions
const generateCharactersSlideWithAI = async (analysis, projectTitle, userId = null, advancedData = {}) => {
  // Show first 2 characters on main slide, rest go to supporting cast (layout only supports 2)
  const maxMainCharacters = 2
  const mainCharacters = advancedData.characters && advancedData.characters.length > 0 
    ? analysis.characters.slice(0, maxMainCharacters) // Show first 2 user characters
    : analysis.characters.slice(0, 2) // Default to 2 AI characters if no user characters
  
  console.log(`👥 Character slide will show ${mainCharacters.length} characters (${advancedData.characters?.length || 0} user-provided)`)
  const theme = getGenreTheme(analysis)
  
  // Generate AI images for characters asynchronously
  const characterImages = await Promise.all(
    mainCharacters.map(async (character, index) => {
      // Check if user uploaded a character image for this character
      if (advancedData.characters && advancedData.characters.length > 0) {
        const uploadedCharacter = advancedData.characters.find(char =>
          char.name.toLowerCase() === character.name.toLowerCase() ||
          char.role.toLowerCase() === character.role.toLowerCase()
        )

        if (uploadedCharacter && uploadedCharacter.imageId) {
          try {
            // Fetch the uploaded image URL from user_image_library
            const { data: imageRecord, error } = await supabase
              .from('user_image_library')
              .select('file_url')
              .eq('id', uploadedCharacter.imageId)
              .single()

            if (!error && imageRecord?.file_url) {
              console.log(`🎯 SUCCESS: Using user-uploaded image for ${character.name} instead of AI generation`)
              console.log(`📸 Image URL: ${imageRecord.file_url}`)
              return imageRecord.file_url
            }
          } catch (error) {
            console.error(`Error fetching uploaded image for ${character.name}:`, error)
          }
        }
      }

      // Generate AI image if no uploaded image found
      try {
        const characterPrompt = createCharacterImagePrompt(character, analysis, projectTitle)
        console.log(`🎨 Generating AI image for ${character.name}...`)

        // Generate square character portrait (reduced ~256x256 for speed)
        const imageData = await imageService.generateImage({
          prompt: characterPrompt,
          type: 'character',
          userId: userId || 'slide-generator',
          projectTitle: projectTitle
        })

        const fallback = getCharacterImage(character)
        const safeUrl = imageData?.url && imageData.url.startsWith('http') ? imageData.url : fallback
        return safeUrl
      } catch (error) {
        console.error(`Failed to generate image for ${character.name}:`, error)
        return getCharacterImage(character)
      }
    })
  )

  return {
    id: `characters-${Date.now()}`,
    name: 'Characters',
    type: 'content',
    elements: [
      {
        id: `characters-title-${Date.now()}`,
        type: 'text',
        content: 'CHARACTERS',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: getTextColor(theme, true),
          textAlign: 'center',
          position: { x: 50, y: 30 },
          width: 700,
          height: 50
        }
      },
      // First Character
      {
        id: `char1-image-${Date.now()}`,
        type: 'image',
        src: characterImages[0],
        style: {
          position: { x: 65, y: 110 },
          width: 225,
          height: 225,
          borderRadius: '12px'
        }
      },
      {
        id: `char1-name-${Date.now()}`,
        type: 'text',
        content: `${mainCharacters[0].name.toUpperCase()} (${mainCharacters[0].role})`,
        style: {
          fontSize: '20px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'left',
          position: { x: 65, y: 360 },
          width: 225,
          height: 30
        }
      },
      {
        id: `char1-details-${Date.now()}`,
        type: 'list',
        content: generateCharacterDetails(mainCharacters[0]),
        style: {
          fontSize: '13px',
          color: theme.textDark,
          lineHeight: '1.4',
          position: { x: 65, y: 395 },
          width: 260,
          height: 180
        }
      },
      // Second Character (if exists)
      ...(mainCharacters[1] ? [
        {
          id: `char2-image-${Date.now()}`,
          type: 'image',
          src: characterImages[1],
          style: {
            position: { x: 510, y: 110 },
            width: 225,
            height: 225,
            borderRadius: '12px'
          }
        },
        {
          id: `char2-name-${Date.now()}`,
          type: 'text',
          content: `${mainCharacters[1].name.toUpperCase()} (${mainCharacters[1].role})`,
          style: {
            fontSize: '20px',
            fontWeight: 'bold',
            color: theme.textLight,
            textAlign: 'left',
            position: { x: 510, y: 360 },
            width: 225,
            height: 30
          }
        },
        {
          id: `char2-details-${Date.now()}`,
          type: 'list',
          content: generateCharacterDetails(mainCharacters[1]),
        style: {
          fontSize: '13px',
          color: theme.textDark,
          lineHeight: '1.4',
          position: { x: 475, y: 395 },
          width: 260,
          height: 180
        }
        }
      ] : [])
    ],
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateSupportingCastSlideWithAI = async (analysis, projectTitle, userId = null, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  // Get remaining user characters (after first 2 used in main character slide)
  const maxMainCharacters = 2
  const remainingUserCharacters = advancedData.characters && advancedData.characters.length > maxMainCharacters 
    ? analysis.characters.slice(maxMainCharacters) // Remaining user characters
    : []
  
  console.log(`🎭 Supporting cast will show ${remainingUserCharacters.length} additional user characters`)
  
  // Combine remaining user characters with AI-generated supporting cast
  let supportingCast = []
  
  // Add remaining user characters first
  if (remainingUserCharacters.length > 0) {
    supportingCast = remainingUserCharacters.map(char => ({
      name: char.name,
      role: char.role,
      description: char.description || char.bio || `${char.name} plays a crucial role as ${char.role}`,
      physicalDescription: char.bio || `Character appearance to be determined`,
      imageId: char.imageId, // Store user's uploaded image ID
      isUserCharacter: true
    }))
  }
  
  // Add AI-generated supporting cast if needed (fill to 2-3 total)
  const aiSupportingCast = analysis.supportingCast?.map(sc => ({
    ...sc,
    description: (() => {
      if (!sc.description) return sc.description
      const words = sc.description.split(' ')
      if (words.length >= 15) return sc.description
      const padding = ' who plays a meaningful role in advancing the story and deepening character relationships.'
      return (sc.description + padding).split(' ').slice(0, 18).join(' ')
    })(),
    isUserCharacter: false
  })) || [
    { 
      name: 'Supporting Character 1', 
      role: 'Mentor', 
      description: 'Experienced guide who offers sharp insight, steady encouragement, and decisive action when it matters most.',
      physicalDescription: 'Middle-aged wise figure with authoritative presence',
      isUserCharacter: false
    },
    { 
      name: 'Supporting Character 2', 
      role: 'Ally', 
      description: 'Loyal companion who brings unique skills, humor, and unwavering support, often bridging conflicts between others.',
      physicalDescription: 'Young energetic supporter with distinctive style',
      isUserCharacter: false
    }
  ]
  
  // Combine user characters with AI characters (user characters first)
  supportingCast = [...supportingCast, ...aiSupportingCast].slice(0, 3)

  // Generate AI images for supporting characters (or use user-uploaded images)
  const supportingImages = await Promise.all(
    supportingCast.slice(0, 3).map(async (character, index) => {
      // Check if this is a user character with uploaded image
      if (character.isUserCharacter && character.imageId) {
        try {
          const { data: imageRecord, error } = await supabase
            .from('user_image_library')
            .select('file_url')
            .eq('id', character.imageId)
            .single()

          if (!error && imageRecord?.file_url) {
            console.log(`🎯 SUCCESS: Using user-uploaded image for supporting character ${character.name}`)
            return imageRecord.file_url
          }
        } catch (error) {
          console.error(`Error fetching uploaded image for supporting character ${character.name}:`, error)
        }
      }

      try {
        // Enhanced character prompt for supporting cast with extra style consistency
        const visualStyle = determineVisualStyle(analysis)
        const characterPrompt = `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

SUPPORTING CHARACTER PORTRAIT: ${character.name}, a ${character.role} from "${projectTitle}". ${character.physicalDescription || `${character.age || 25}-year-old ${character.role.toLowerCase()}, professional appearance`}.

CRITICAL STYLE CONSISTENCY:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}  
- Genre: ${analysis.genres?.join(', ') || 'drama'} genre aesthetics
- MUST match the exact same visual style as main characters in this project
- Same lighting, color palette, artistic approach as other "${projectTitle}" character portraits
- Make this character clearly distinct from other characters (unique facial features, hairstyle, wardrobe)

Technical: Square format (~256x256), high quality, suitable for pitch deck character profile.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.
CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure character portrait only in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`

        console.log(`🎨 Generating AI image for supporting character ${character.name}...`)
        
        const imageData = await imageService.generateImage({
          prompt: characterPrompt,
          type: 'character',
          userId: userId || 'slide-generator',
          projectTitle: projectTitle
        })
        
        return imageData?.url || getSupportingCharacterImage(index + 1)
      } catch (error) {
        console.error(`Failed to generate image for ${character.name}:`, error)
        return getSupportingCharacterImage(index + 1)
      }
    })
  )

  const elements = [
    {
      id: `supporting-title-${Date.now()}`,
      type: 'text',
      content: 'SUPPORTING CAST',
      style: {
        fontSize: '36px',
        fontWeight: 'bold',
        color: theme.textLight,
        textAlign: 'center',
        position: { x: 50, y: 30 },
        width: 700,
        height: 50
      }
    }
  ]

  // Add supporting characters dynamically
  supportingCast.slice(0, 3).forEach((character, index) => {
    const yPos = 130 + (index * 180)
    const xPos = index === 2 ? 500 : 100
    const contentXPos = index === 2 ? 670 : 270
    const contentWidth = index === 2 ? 150 : 300

    elements.push(
      {
        id: `support${index + 1}-image-${Date.now()}`,
        type: 'image',
        src: supportingImages[index],
        style: {
          position: { x: xPos, y: yPos },
          width: 150,
          height: 150,
          borderRadius: '12px'
        }
      },
      {
        id: `support${index + 1}-name-${Date.now()}`,
        type: 'text',
        content: `${character.name} (${character.role})`,
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: theme.textLight,
          textAlign: 'left',
          position: { x: contentXPos, y: yPos },
          width: contentWidth,
          height: 30
        }
      },
      {
        id: `support${index + 1}-desc-${Date.now()}`,
        type: 'text',
        content: character.description,
        style: {
          fontSize: '14px',
          color: theme.textDark,
          lineHeight: '1.4',
          position: { x: contentXPos, y: yPos + 52 },
          width: contentWidth,
          height: 120
        }
      }
    )
  })

  return {
    id: `supporting-cast-${Date.now()}`,
    name: 'Supporting Cast',
    type: 'content',
    elements,
    background: {
      type: 'gradient',
      value: theme.gradient
    }
  }
}

const generateProductionApproachSlideWithAI = async (analysis, projectTitle, userId = null, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  // Generate production approach background image
  let backgroundImage = 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&h=600&fit=crop'

  // Try to use a reference image first (production slide is typically slide 6)
  const referenceImage = await getReferenceImageForSlide('production', 5, advancedData)
  if (referenceImage) {
    backgroundImage = referenceImage
  } else {
    // Generate AI image if no reference image available
    try {
      const productionPrompt = createProductionImagePrompt(analysis, projectTitle)
      console.log('🎨 Generating production approach background image...')

      const imageData = await imageService.generateImage({
        prompt: productionPrompt,
        type: 'scene',
        userId: userId || 'slide-generator',
        projectTitle: projectTitle
      })

      if (imageData?.url) {
        backgroundImage = imageData.url
      }
    } catch (error) {
      console.error('Failed to generate production approach image:', error)
    }
  }

  return {
    id: `production-${Date.now()}`,
    name: 'Production Approach',
    type: 'content',
    elements: [
      {
        id: `production-title-${Date.now()}`,
        type: 'text',
        content: 'PRODUCTION APPROACH',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 30 },
          width: 700,
          height: 50
        }
      },
      {
        id: `production-content-${Date.now()}`,
        type: 'list',
        content: (() => {
          let content = null
          
          // Check if productionApproach exists and has meaningful content
          if (analysis.productionApproach && Array.isArray(analysis.productionApproach) && analysis.productionApproach.length > 0) {
            content = analysis.productionApproach
          } else if (analysis.productionApproach && typeof analysis.productionApproach === 'string' && analysis.productionApproach.trim().length > 0) {
            content = [analysis.productionApproach]
          } else {
            // Use fallback content
            content = getProductionDetails(analysis)
          }
          
          console.log('🔍 Production Approach Slide - Analysis received:', {
            productionApproach: analysis.productionApproach,
            hasProductionApproach: !!analysis.productionApproach,
            productionApproachType: typeof analysis.productionApproach,
            productionApproachLength: analysis.productionApproach?.length,
            fallbackDetails: getProductionDetails(analysis),
            finalContent: content,
            finalContentType: typeof content,
            finalContentLength: content?.length,
            isArray: Array.isArray(content)
          })
          return content
        })(),
        style: {
          fontSize: '18px',
          color: '#ffffff',
          lineHeight: '1.6',
          position: { x: 50, y: 160 },
          width: 700,
          height: 300
        }
      }
    ],
    background: {
      type: 'image',
      url: backgroundImage,
      position: 'center',
      size: 'cover'
    }
  }
}

const generateTalentStrategySlideWithAI = async (analysis, projectTitle, userId = null, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  // Generate talent strategy background image
  let backgroundImage = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop'
  
  // Try to use a reference image first (talent slide is typically slide 7)
  const referenceImage = await getReferenceImageForSlide('talent', 6, advancedData)
  if (referenceImage) {
    backgroundImage = referenceImage
    console.log('🎬 Using reference image for talent strategy slide background')
  } else {
    // Generate AI image only if no reference image available
    try {
      const talentPrompt = createTalentStrategyImagePrompt(analysis, projectTitle)
      console.log('🎨 Generating talent strategy background image...')
      
      const imageData = await imageService.generateImage({
        prompt: talentPrompt,
        type: 'scene',
        userId: userId || 'slide-generator',
        projectTitle: projectTitle
      })
      
      if (imageData?.url) {
        backgroundImage = imageData.url
      }
    } catch (error) {
      console.error('Failed to generate talent strategy image:', error)
    }
  }

  return {
    id: `talent-${Date.now()}`,
    name: 'Talent Strategy',
    type: 'content',
    elements: [
      {
        id: `talent-title-${Date.now()}`,
        type: 'text',
        content: 'TALENT STRATEGY',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 30 },
          width: 700,
          height: 50
        }
      },
      {
        id: `talent-content-${Date.now()}`,
        type: 'list',
        content: (() => {
          let content = null
          
          // Check if talentStrategy exists and has meaningful content
          if (analysis.talentStrategy && Array.isArray(analysis.talentStrategy) && analysis.talentStrategy.length > 0) {
            content = analysis.talentStrategy
          } else if (analysis.talentStrategy && typeof analysis.talentStrategy === 'string' && analysis.talentStrategy.trim().length > 0) {
            content = [analysis.talentStrategy]
          } else {
            // Use fallback content
            content = getTalentDetails(analysis)
          }
          
          console.log('🔍 Talent Strategy Slide - Analysis received:', {
            talentStrategy: analysis.talentStrategy,
            hasTalentStrategy: !!analysis.talentStrategy,
            talentStrategyType: typeof analysis.talentStrategy,
            talentStrategyLength: analysis.talentStrategy?.length,
            fallbackDetails: getTalentDetails(analysis),
            finalContent: content,
            finalContentType: typeof content,
            finalContentLength: content?.length,
            isArray: Array.isArray(content)
          })
          return content
        })(),
        style: {
          fontSize: '18px',
          color: '#ffffff',
          lineHeight: '1.6',
          position: { x: 50, y: 160 },
          width: 700,
          height: 300
        }
      }
    ],
    background: {
      type: 'image',
      url: backgroundImage,
      position: 'center',
      size: 'cover'
    }
  }
}

// Generate Budget slide with AI-generated background
const generateBudgetSlideWithAI = async (analysis, projectTitle, userId = null, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  // Generate budget background image
  let backgroundImage = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop'
  
  // Try to use a reference image first (budget slide is typically slide 8)
  const referenceImage = await getReferenceImageForSlide('budget', 7, advancedData)
  if (referenceImage) {
    backgroundImage = referenceImage
    console.log('💰 Using reference image for budget slide background')
  } else {
    // Generate AI image only if no reference image available
    try {
      const budgetPrompt = createBudgetImagePrompt(analysis, projectTitle)
      console.log('🎨 Generating budget slide background image...')
      
      const imageData = await imageService.generateImage({
        prompt: budgetPrompt,
        type: 'scene',
        userId: userId || 'slide-generator',
        projectTitle: projectTitle
      })
      
      if (imageData?.url) {
        backgroundImage = imageData.url
      }
    } catch (error) {
      console.error('Failed to generate budget background image:', error)
    }
  }
  
  // Generate budget details based on analysis
  const budgetDetails = getBudgetDetails(analysis)
  
  return {
    id: `budget-${Date.now()}`,
    name: 'Budget',
    type: 'content',
    elements: [
      {
        id: `budget-title-${Date.now()}`,
        type: 'text',
        content: 'BUDGET',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 30 },
          width: 700,
          height: 50
        }
      },
      {
        id: `budget-content-${Date.now()}`,
        type: 'list',
        content: budgetDetails,
        style: {
          fontSize: '18px',
          color: '#ffffff',
          lineHeight: '1.6',
          position: { x: 50, y: 160 },
          width: 700,
          height: 300
        }
      }
    ],
    background: {
      type: 'image',
      url: backgroundImage,
      position: 'center',
      size: 'cover'
    }
  }
}

// Helper functions for creating AI image prompts
const createCharacterImagePrompt = (character, analysis, projectTitle) => {
  const userBio = character.bio || character.description || ''
  const physicalDesc = character.physicalDescription || `${character.age || 25}-year-old ${character.role.toLowerCase()}, professional appearance`
  const projectGenres = analysis.genres?.join(', ') || 'drama'
  const visualStyle = determineVisualStyle(analysis)
  
  // Include user bio in character description if available
  let characterDesc = `${character.name}, a ${character.role} from "${projectTitle}". ${physicalDesc}.`
  if (userBio && userBio.trim()) {
    characterDesc += ` Character background: ${userBio.trim()}.`
  }
  
  return `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

Character portrait of ${characterDesc}
Make this character clearly distinct from other characters in this project (unique facial features, hairstyle, wardrobe, silhouette).
${userBio ? `Ensure the character's appearance reflects their background: ${userBio.substring(0, 150)}.` : ''}

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${projectGenres} genre aesthetics with appropriate mood and atmosphere
- Expression should reflect their personality as ${character.role}
- Professional character design suitable for ${analysis.format || 'production'}

Technical: Square format (~256x256), high quality, suitable for pitch deck character profile.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure character portrait only in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
}

const createProductionImagePrompt = (analysis, projectTitle) => {
  const genres = analysis.genres?.join(', ') || 'drama'
  const synopsis = analysis.synopsis?.substring(0, 200) || ''
  const visualStyle = determineVisualStyle(analysis)
  
  return `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

Production scene from "${projectTitle}". ${synopsis ? `Story context: ${synopsis}...` : ''}

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${genres} genre aesthetics with appropriate mood and atmosphere
- Representative scene from the production showing the world/environment
- Professional production quality

Technical: Landscape format (800x600), high quality background suitable for slide overlay.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure scene content only in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
}

const createTalentStrategyImagePrompt = (analysis, projectTitle) => {
  const genres = analysis.genres?.join(', ') || 'drama'
  const synopsis = analysis.synopsis?.substring(0, 200) || ''
  const visualStyle = determineVisualStyle(analysis)
  
  return `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

Dramatic moment from "${projectTitle}". ${synopsis ? `Story context: ${synopsis}...` : ''}

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${genres} genre atmosphere
- Key scene showcasing main characters demonstrating performance quality and star appeal
- Multiple characters in compelling interaction, showing emotional depth and talent

Technical: Landscape format (800x600), high quality background suitable for slide overlay.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure dramatic scene in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
}

const createBudgetImagePrompt = (analysis, projectTitle) => {
  const genres = analysis.genres?.join(', ') || 'drama'
  const synopsis = analysis.synopsis?.substring(0, 200) || ''
  const visualStyle = determineVisualStyle(analysis)
  
  return `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${projectTitle}" project.

Wide establishing shot from "${projectTitle}" production. ${synopsis ? `Story context: ${synopsis}...` : ''}

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${genres} genre atmosphere
- Wide cinematic establishing shot showing production scale and scope
- Professional film production environment or key location
- Expansive view showcasing the project's visual ambition and production value

Technical: Landscape format (800x600), high quality background suitable for slide overlay.

CRITICAL: ALL images for "${projectTitle}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure wide shot scene in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
}

const getBudgetDetails = (analysis) => {
  const format = analysis.format || 'Feature Film'
  const genres = analysis.genres?.join('/') || 'Drama'
  const runtime = analysis.runtime || analysis.length || '90-120'
  
  // Determine budget based on format and genre
  let budget = '£1.2mil'
  let shootDays = '21 days'
  let castSize = '5 people'
  let locations = 'Malta / Gozo / N. Ireland'
  
  if (format.includes('Micro') || format.includes('Short')) {
    budget = '£150k - £300k'
    shootDays = '8-12 days'
    castSize = '3-4 people'
    locations = 'Local / Studio'
  } else if (format.includes('Series') || format.includes('TV')) {
    budget = '£800k - £2.5mil'
    shootDays = '45-60 days'
    castSize = '8-12 people'
    locations = 'Multiple locations'
  } else if (format.includes('Feature')) {
    if (genres.includes('Action') || genres.includes('Horror') || genres.includes('Thriller')) {
      budget = '£1.2mil - £3.8mil'
      shootDays = '28-42 days'
      castSize = '6-10 people'
      locations = 'International / Studio'
    } else {
      budget = '£800k - £2.2mil'
      shootDays = '21-35 days'
      castSize = '4-8 people'
      locations = 'Regional / Studio'
    }
  }
  
  // Format runtime properly
  let formattedRuntime = typeof runtime === 'string' ? runtime : `${runtime} mins`
  if (!formattedRuntime.includes('min')) {
    formattedRuntime += ' mins'
  }
  
  return [
    `GENRE: ${genres}`,
    `BUDGET: ${budget}`,
    `LENGTH: ${formattedRuntime}`,
    `PRODUCTION SHOOT: ${shootDays}`,
    `CAST: ${castSize}`,
    `LOCATIONS: ${locations}`
  ]
}

const getProductionDetails = (analysis) => {
  const format = analysis.format || 'Feature Film'
  const genres = analysis.genres?.join(', ') || 'Drama'
  
  if (format.includes('Micro') || format.includes('Series')) {
    return [
      '100% AI-generated content with cutting-edge visual effects',
      '',
      'Real-video references using actual influencer voices',
      '',
      'Authentic performances maintaining "human soul" at story\'s core',
      '',
      'Benefits:',
      '',
      'Cost-effective: 70% reduction vs traditional production',
      '',
      'Rapid turnaround: Complete series in 6-8 weeks',
      '',
      'Scalable: Easy to produce sequels/spin-offs',
      '',
      'Consistent quality: No scheduling conflicts or location limitations'
    ]
  }
  
  return [
    'Hybrid Traditional-Digital Production Approach',
    '',
    `Professional ${genres} cinematography standards`,
    '',
    'Cost-efficient studio and location production',
    '',
    'Experienced department heads and crew',
    '',
    'Strategic use of practical and digital effects',
    '',
    'Post-production excellence with industry-standard workflow',
    '',
    'Timeline: 12-18 months from pre-production to delivery'
  ]
}

// Generate contact slide with user profile data
const generateContactSlide = async (analysis, userId = null, advancedData = {}) => {
  const theme = getGenreTheme(analysis)
  
  console.log('📞 Generating contact slide with advanced data:', {
    creator: advancedData.creator,
    creatorCompany: advancedData.creatorCompany
  })
  
  // Get user's email from auth context
  let userEmail = null
  if (userId) {
    try {
      const { supabase } = await import('../lib/supabase.js')
      const { data: { user }, error } = await supabase.auth.getUser()
      if (!error && user) {
        userEmail = user.email
      }
    } catch (error) {
      console.warn('Could not get user email from auth:', error)
    }
  }

  // Fetch user profile data for social links and website
  let userProfile = null
  if (userId) {
    try {
      const { supabase } = await import('../lib/supabase.js')
      const { data: profile, error } = await supabase
        .from('users')
        .select('website, instagram, twitter, youtube')
        .eq('id', userId)
        .single()

      if (!error && profile) {
        userProfile = profile
        console.log('✅ Retrieved user profile for contact slide:', profile)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }
  
  // Use advanced form data for contact info with fallbacks
  const contactInfo = {
    name: advancedData.creator || 'Your Name',
    title: 'Writer/Director/Producer',
    email: userEmail || 'contact@email.com',
    website: (() => {
      // Priority: User profile website > Company-generated website > Default
      if (userProfile?.website) {
        console.log('🌐 Using user profile website:', userProfile.website)
        return userProfile.website
      }
      if (advancedData.creatorCompany) {
        let company = advancedData.creatorCompany.toLowerCase().replace(/\s+/g, '')
        // Remove existing .com to prevent double .com
        company = company.replace(/\.com$/, '')
        return `www.${company}.com`
      }
      return 'www.yourwebsite.com'
    })(),
    phone: '+1 (555) 123-4567',
    location: 'Los Angeles, CA',
    social: {
      twitter: userProfile?.twitter || null,
      instagram: userProfile?.instagram || null,
      linkedin: null, // Not stored in current schema
      tiktok: null // Not stored in current schema
    }
  }
  
  // Generate AI background for contact slide
  let backgroundImage = null
  try {
    const visualStyle = determineVisualStyle(analysis)
    const contactPrompt = `STRICT VISUAL STYLE: ${visualStyle.style} - This MUST be maintained consistently throughout the entire "${analysis.title}" project.

Powerful cinematic background for "${analysis.title}" contact slide.

VISUAL CONSISTENCY REQUIREMENTS:
- Style: ${visualStyle.style}
- Description: ${visualStyle.description}
- Genre: ${analysis.genres?.join(', ') || 'cinematic'} atmosphere that sells the vision
- Professional and compelling visual that represents the project's essence and makes viewers want to reach out

Technical: Landscape format (800x600), suitable for text overlay.

CRITICAL: ALL images for "${analysis.title}" MUST use ${visualStyle.style} style. NO style variations allowed.

CRITICAL: ABSOLUTELY NO TEXT, NO TITLES, NO WORDS, NO LETTERS, NO CAPTIONS on the image. 
FORBIDDEN: Any writing, typography, subtitles, or text overlay.
REQUIRED: Pure atmospheric background in ${visualStyle.style}. 
IMAGE MUST BE: Clean visual content without any textual elements.`
    
    console.log('🎨 Generating contact slide background...')
    
    const imageData = await imageService.generateImage({
      prompt: contactPrompt,
      type: 'scene',
      userId: userId || 'slide-generator',
      projectTitle: analysis.title
    })
    
    if (imageData?.url) {
      backgroundImage = imageData.url
    }
  } catch (error) {
    console.error('Failed to generate contact background image:', error)
  }
  
  return {
    id: `contact-${Date.now()}`,
    name: 'Contact',
    type: 'content',
    elements: [
      {
        id: `contact-title-${Date.now()}`,
        type: 'text',
        content: 'CONTACT',
        style: {
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 50 },
          width: 700,
          height: 50
        }
      },
      {
        id: `contact-name-${Date.now()}`,
        type: 'text',
        content: contactInfo.name.toUpperCase(),
        style: {
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 120 },
          width: 700,
          height: 40
        }
      },
      {
        id: `contact-title-${Date.now()}`,
        type: 'text',
        content: contactInfo.title,
        style: {
          fontSize: '20px',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 170 },
          width: 700,
          height: 30
        }
      },
      // Website as its own link element if available
      ...(contactInfo.website ? [{
        id: `contact-website-${Date.now()}`,
        type: 'text',
        content: contactInfo.website,
        style: {
          fontSize: '16px',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 205 },
          width: 700,
          height: 30,
          textDecoration: 'underline'
        }
      }] : []),
      // Phone as centered text element
      {
        id: `contact-phone-${Date.now()}`,
        type: 'text',
        content: contactInfo.phone,
        style: {
          fontSize: '16px',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 250 },
          width: 700,
          height: 30
        }
      },
      // Address as centered text element  
      {
        id: `contact-address-${Date.now()}`,
        type: 'text',
        content: contactInfo.location,
        style: {
          fontSize: '16px',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 290 },
          width: 700,
          height: 30
        }
      },
      // Email 
      ...([{
        id: `contact-email-${Date.now()}`,
        type: 'text',
        content: contactInfo.email,
        style: {
          fontSize: '16px',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 330 },
          width: 700,
          height: 30
        }
      }]),
      // Social media list if available
      ...(() => {
        const socialMedia = []
        if (contactInfo.social.twitter) socialMedia.push(`🐦 @${contactInfo.social.twitter}`)
        if (contactInfo.social.instagram) socialMedia.push(`📸 @${contactInfo.social.instagram}`)
        if (contactInfo.social.linkedin) socialMedia.push(`💼 ${contactInfo.social.linkedin}`)
        if (contactInfo.social.tiktok) socialMedia.push(`🎵 @${contactInfo.social.tiktok}`)
        
        if (socialMedia.length > 0) {
          return [{
            id: `contact-social-media-${Date.now()}`,
            type: 'list',
            content: socialMedia,
            style: {
              fontSize: '16px',
              color: '#ffffff',
              lineHeight: '1.8',
              textAlign: 'center',
              position: { x: 50, y: 380 },
              width: 700,
              height: 120
            }
          }]
        }
        return []
      })(),
      {
        id: `contact-social-${Date.now()}`,
        type: 'text',
        content: 'Let\'s bring this vision to life together.',
        style: {
          fontSize: '16px',
          fontStyle: 'italic',
          color: '#ffffff',
          textAlign: 'center',
          position: { x: 50, y: 500 },
          width: 700,
          height: 30
        }
      }
    ],
    background: backgroundImage ? {
      type: 'image',
      url: backgroundImage,
      position: 'center',
      size: 'cover'
    } : {
      type: 'gradient',
      value: theme.gradient
    }
  }
}
